import Image from 'next/image';
import React from 'react';
import { Virtual, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CarouselControls, CarouselIndicators, ZoomControls } from '@/entities/image-carousel';
import { blurDataURL } from '@/shared/lib';
import { useImageErrors, useImageSwiperNavigation } from '@/shared/lib/hooks';

import 'swiper/css/zoom';

interface Props {
  pictures: string[];
  toggleFullSize: () => void;
  currentImageIndex: number;
  handleImageCurrentIndex: (index: number) => void;
}

export default function FullScreenView({
  pictures,
  toggleFullSize,
  currentImageIndex,
  handleImageCurrentIndex
}: Props) {
  const { imageErrors, handleImageError } = useImageErrors(pictures);
  const { swiperRef, handlePrev, handleNext } = useImageSwiperNavigation();

  return (
    <div className="fixed inset-0 z-50 bg-black/90">
      <section className="relative m-auto flex h-[90vh] w-screen flex-col items-center justify-center">
        <ZoomControls toggleFullSize={toggleFullSize} fullSized={true} />
        <Swiper
          modules={[Virtual, Zoom]}
          zoom
          initialSlide={currentImageIndex}
          onSlideChange={(swiper) => handleImageCurrentIndex(swiper.activeIndex)}
          ref={swiperRef}
          effect="fade"
          className="h-full w-full"
        >
          {pictures.map((picture, index) => (
            <SwiperSlide key={`carousel-full-size-${index}`}>
              <div className="swiper-zoom-container absolute flex h-full items-center justify-center rounded-2xl">
                <Image
                  src={imageErrors[index] ? '/broken-image.svg' : picture}
                  alt={`carousel-full-size-${index}`}
                  className="rounded-2xl p-2"
                  quality={100}
                  priority
                  fill
                  placeholder="blur"
                  blurDataURL={blurDataURL}
                  onError={() => handleImageError(index)}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>
      <CarouselControls goToNextImage={handleNext} goToPreviousImage={handlePrev} />
      <CarouselIndicators currentIndex={currentImageIndex} length={pictures.length} />
    </div>
  );
}
